import type {
    AccountInfo,
    InteractionStatus,
    PublicClientApplication,
} from '@azure/msal-browser';
import type { Ref } from 'vue';
import { useMsalStore } from '../stores/msal-store';
import { storeToRefs } from 'pinia';

export type MsalContext = {
    /**
     * The MSAL-instance used in this application
     */
    msal: PublicClientApplication;
    /**
     * The currenty authenticated accounts.
     */
    accounts: Ref<AccountInfo[]>;
    /**
     * The current interaction status of the msal instance.
     */
    interactionStatus: Ref<InteractionStatus>;
    /**
     * Whether the interactionStatus is `InteractionStatus.None`.
     */
    idle: Ref<boolean>;
    /**
     * Whether the msal instance is in an interaction-status that will
     * change automatically to the idle state.
     *
     * Interaction statuses that apply are:
     *  - `InteractionStatus.Login`
     *  - `InteractionStatus.Startup`
     *  - `InteractionStatus.HandleRedirect`
     *  - `InteractionStatus.SsoSilent`
     */
    loading: Ref<boolean>;
    /**
     * The interaction that is currently in progress.
     *
     * @deprecated use `interactionStatus`
     */
    inProgress: Ref<InteractionStatus>;
};

export function useMsal(): MsalContext {
    const store = useMsalStore();

    const { msal, accounts, interactionStatus, idle, loading } =
        storeToRefs(store);

    // eslint-disable-next-line vue/no-ref-as-operand

    store.handleRedirectPromise();

    return {
        msal: msal.value as PublicClientApplication,
        accounts,
        interactionStatus,
        inProgress: interactionStatus,
        idle,
        loading,
    };
}
